import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, CircularProgress } from '@mui/material';

import { BackdropLoading } from '../../contexts/Backgrop/Backdrop';
import { useOfferDetailQuery, useUpdateOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import { EditOfferForm } from './EditOfferForm';
import { mapToOfferForm, mapToUpdateOfferInput, schema } from './components/schema';

const EditOffer: React.FC = () => {
  const navigate = useNavigate();
  const { offerId } = useParams<{ offerId: string }>();
  const venueState = useAppSelector((state) => state.venue.value);

  const { subscription } = useAppSelector((state) => state.subscription.value);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const { data } = useOfferDetailQuery(offerId);

  React.useEffect(() => {
    if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      navigate(`/products/${offerId}/edit`);
    }
  }, [navigate, venueState, offerId]);

  const mutation = useUpdateOfferQuery(venueState?.id);

  const initialValues = React.useMemo(() => data && mapToOfferForm(data), [data]);
  if (!initialValues) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <EditOfferForm
        method='update'
        formikConfig={{
          initialValues,
          validationSchema: schema,
          onSubmit: async (values) => {
            if (data?.id) {
              const input = mapToUpdateOfferInput(data.id, values, data.timeZoneId, subscriptionPlan);

              await mutation.mutateAsync(input);
            }
          },
        }}
      />
      {mutation.isPending && <BackdropLoading open />}
    </>
  );
};

export default EditOffer;
